import type { Compendium } from "./compendium";
import { ITEM_SCHEMAS, type ItemOf, type ItemType } from "./schemas";

/**
 * Missing-content detection.
 *
 * Pilots, mechs, encounters and the VTT all resolve items out of the Compendium
 * by `(type, id)`. When a saved sheet references an id that NO loaded pack
 * provides — typically because the pack it came from (a paid NPC `.lcp`, a
 * community expansion) was never installed — we must fail LOUDLY and
 * descriptively, naming the type+id and the LCP file the content should have
 * lived in, rather than silently rendering an empty slot.
 */

/**
 * Thrown by {@link requireById} when a referenced `(type, id)` is not provided by
 * any loaded pack. Carries the structured fields so the UI can render a precise
 * prompt ("install the pack that ships npc_classes.json") instead of a stack trace.
 */
export class MissingContentError extends Error {
  readonly type: ItemType;
  readonly id: string;
  /** The LCP member file this type lives in (e.g. `npc_classes.json`). */
  readonly expectedFile: string;
  readonly context?: string;

  constructor(type: ItemType, id: string, context?: string) {
    const expectedFile = ITEM_SCHEMAS[type].file;
    const where = context ? ` (referenced by ${context})` : "";
    super(
      `Missing content: no loaded pack provides ${type} "${id}"${where}. ` +
        `It should come from an LCP that ships ${expectedFile}; install or load that pack.`,
    );
    this.name = "MissingContentError";
    this.type = type;
    this.id = id;
    this.expectedFile = expectedFile;
    this.context = context;
  }
}

/**
 * Resolve an item by `(type, id)` or throw a descriptive {@link MissingContentError}.
 * Use this wherever a reference *must* resolve (loading a saved pilot/mech); use
 * the compendium's plain `getById` where absence is acceptable.
 */
/** Named arguments for {@link requireById}. */
export type RequireByIdArgs<T extends ItemType> = {
  compendium: Compendium;
  type: T;
  id: string;
  context?: string;
}

export function requireById<T extends ItemType>(args: RequireByIdArgs<T>): ItemOf<T> {
  const { compendium, type, id, context } = args;
  const item = compendium.getById(type, id);
  if (item === undefined) throw new MissingContentError(type, id, context);
  return item;
}
